import { useEffect, useMemo, useRef, useState } from "react";

import { ArrowDownIcon, MapPolygonIcon, POIIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import { MESSAGE } from "@/constants/message";
import {
  GEOHASH_RESOLUTION_LABEL,
  H3_RESOLUTION_META
} from "@/constants/spatialResolution";
import { useModal } from "@/context/ModalProvider";
import { cn } from "@/lib/utils";
import {
  buildTreeFromArray,
  mapFeaturesToPolygons
} from "@/pages/analysisHistory/utils";
import {
  AdmAreaInput,
  AnalysisHistoryDetailResponse,
  AnalysisHistoryInputType,
  AreaInput,
  GeohashResolution,
  H3Resolution,
  OutputType
} from "@/types/analysisHistory";
import {
  ECatchmentTransport,
  EOutputCoverageType,
  RoadExclusionsLabel
} from "@/types/spatial";

import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

import Badge from "../ui/badge";
import LoadingWrapper from "../ui/LoadingWrapper";
import { AnalysisHistoryDatasetList } from "./AnalysisHistoryDatasetList";
import { DetailAdminTree } from "./DetailAdminTree";
import { EmptyState } from "./EmptyState";

const COLOR_VARIANTS = {
  green: "bg-green-50 border-green-200 text-green-700",
  purple: "bg-purple-50 border-purple-200 text-purple-700",
  orange: "bg-orange-50 border-orange-200 text-orange-700",
  yellow: "bg-yellow-50 border-yellow-200 text-yellow-700",
  blue: "bg-blue-50 border-blue-200 text-blue-700"
} as const;

type ColorVariant = keyof typeof COLOR_VARIANTS;

const ColorBadge = ({
  color,
  className,
  children
}: {
  color: ColorVariant;
  className?: string;
  children: React.ReactNode;
}) => {
  const [bg, border, text] = COLOR_VARIANTS[color].split(" ");
  return (
    <Badge
      className={cn(
        bg,
        border,
        "rounded-lg flex items-center justify-center",
        className
      )}
    >
      <span className={cn(text, "text-xs font-semibold")}>{children}</span>
    </Badge>
  );
};

const ResolutionSection = ({
  level,
  coverageContent,
  customCatchmentRadiusM
}: {
  level: number;
  coverageContent: React.ReactNode;
  customCatchmentRadiusM?: number;
}) => (
  <div className="flex flex-col gap-2 p-4">
    <p className="font-semibold text-sm mb-2">Resolution</p>
    <div className="relative pl-6 flex flex-col gap-2">
      <div className="absolute left-0 -top-2 bottom-2 border-l border-whisper" />
      <div className="relative grid grid-cols-[2fr_5fr] items-center">
        <span className="absolute -left-6 w-4 border-t border-whisper" />
        <p>Level</p>
        <ColorBadge color="blue" className="w-min">
          {level}
        </ColorBadge>
      </div>
      <div className="relative grid grid-cols-[2fr_5fr] items-center">
        <span className="absolute -left-6 w-4 border-t border-whisper" />
        <p>Coverage</p>
        {coverageContent}
      </div>
      {customCatchmentRadiusM != null && (
        <div className="relative grid grid-cols-[2fr_5fr] items-center">
          <span className="absolute -left-6 w-4 border-t border-whisper" />
          <p>Custom Catchment</p>
          <span className="text-txtHeading font-semibold">
            {customCatchmentRadiusM} m
          </span>
        </div>
      )}
    </div>
  </div>
);

const getH3Coverage = (level: H3Resolution): React.ReactNode => {
  const { radiusKm, areaKm2 } = H3_RESOLUTION_META[level];
  return (
    <p className="font-semibold text-txtBody">
      <span className="font-semibold text-txtHeading">{radiusKm}Km</span>{" "}
      {`(${areaKm2}Km²)`}
    </p>
  );
};

interface DetailSectionProps {
  isLoading?: boolean;
  data?: AnalysisHistoryDetailResponse;
  emptyMessage: { title: string; desc: string };
  onDelete?: (id: string) => void;
  onOpen?: (id: string) => void;
  onRerun?: (id: string) => void;
}

export const DetailSection = ({
  isLoading,
  data,
  emptyMessage,
  onDelete,
  onOpen,
  onRerun
}: DetailSectionProps) => {
  const { showModal } = useModal();
  const contentRef = useRef<HTMLDivElement>(null);
  const [needExpand, setNeedExpand] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleOnOpen = () => {
    if (!data) return;
    const expired = dayjs(data.expired_at).isBefore(dayjs());
    if (expired) {
      showModal("confirm", {
        title: "Data Analysis Expired",
        message: MESSAGE.ANALYSIS_EXPIRED,
        confirmLabel: "Re-Run",
        cancelLabel: "Later",
        onCancel: () => {},
        onConfirm: () => {
          onRerun?.(data.id);
        }
      });
      return;
    }
    onOpen?.(data.id);
  };

  const handleOnDelete = () => {
    if (!data) return;

    showModal("confirm", {
      title: "Are you sure, you want to delete? ",
      message: "Once deleted, this action cannot be undone.",
      cancelLabel: "Cancel",
      confirmLabel: "Delete",
      onConfirm: () => onDelete?.(data.id)
    });
  };

  useEffect(() => {
    const el = contentRef.current;
    if (!el?.parentElement) return;

    const isOverflowing = el.scrollHeight > el.parentElement.clientHeight;
    setNeedExpand(isOverflowing);
  }, [data]);

  const renderInputAreaType = (type?: AreaInput["type"]): React.ReactNode => {
    switch (type) {
      case AnalysisHistoryInputType.ADMIN:
        return <ColorBadge color="green">Administrative Area</ColorBadge>;
      case AnalysisHistoryInputType.CATCHMENT:
        return <ColorBadge color="purple">Catchment</ColorBadge>;
      case AnalysisHistoryInputType.POLYGON:
        return <ColorBadge color="orange">Custom Polygon</ColorBadge>;
      default:
        return null;
    }
  };

  const renderOutputAreaType = (type?: OutputType): React.ReactNode => {
    switch (type) {
      case OutputType.H3:
        return <ColorBadge color="green">H3</ColorBadge>;
      case OutputType.GEOHASH:
        return <ColorBadge color="orange">Geohash</ColorBadge>;
      case OutputType.SITE_PROFILING:
        return <ColorBadge color="yellow">Site Profiling</ColorBadge>;
      default:
        return null;
    }
  };

  const renderGeohashResolution = (level: GeohashResolution): string => {
    return GEOHASH_RESOLUTION_LABEL[level] || "-";
  };

  const byRadius =
    data?.area_input?.type === AnalysisHistoryInputType.CATCHMENT &&
    "catchment" in data.area_input &&
    data.area_input.catchment.profile === "radius";

  const byRoadAccess =
    data?.area_input?.type === AnalysisHistoryInputType.CATCHMENT &&
    "catchment" in data.area_input &&
    data.area_input.catchment.profile === ECatchmentTransport.DRIVING;

  const renderCatchmentType = (): string => {
    if (byRadius) return "By Radius";
    if (byRoadAccess) return "By Road Access";
    return "-";
  };

  const onGetRoadExclusionValue = () => {
    const roadExclusion =
      data?.area_input?.type === AnalysisHistoryInputType.CATCHMENT &&
      "catchment" in data.area_input &&
      data.area_input.catchment.exclusions;
    if (!roadExclusion) return "-";
    if (roadExclusion && !roadExclusion.length) return "-";
    return roadExclusion?.map((item) => RoadExclusionsLabel[item]).join(", ");
  };

  const dataPolygon =
    data?.area_input?.type === AnalysisHistoryInputType.POLYGON &&
    "custom_catchments" in data.area_input &&
    data.area_input.custom_catchments.features;

  const polygons = useMemo(
    () => mapFeaturesToPolygons(dataPolygon || []),
    [dataPolygon]
  );

  const adminHierarchy = buildTreeFromArray(
    (data?.area_input as AdmAreaInput)?.adm_area || []
  );

  // Custom catchment radius — only resolved when the output mode is CUSTOM_CATCHMENT.
  const catchmentRadiusM =
    data?.analysis_output?.output_mode === EOutputCoverageType.CUSTOM_CATCHMENT
      ? data.analysis_output.custom_catchment?.radius_m
      : undefined;

  return (
    <div
      className={twMerge(
        "border border-whisper rounded-lg flex flex-col h-full overflow-hidden",
        data && "border-teal-200"
      )}
    >
      {/* Header */}
      <div
        className={twMerge(
          "bg-white-smoke rounded-t-lg p-4 border-b border-whisper flex-shrink-0",
          data && "border-teal-200 bg-teal-50"
        )}
      >
        <div className="grid grid-cols-2 gap-2 text-txtHeading">
          <div className="flex items-center gap-12">
            <span className="font-semibold">Author Name</span>
            <span>{data?.author_name || "-"}</span>
          </div>
          <div className="flex items-center gap-12">
            <span className="font-semibold">Generated Time</span>
            <span>
              {data?.created_at
                ? dayjs(data.created_at).format("DD MMM YYYY, HH:mm:ss")
                : "-"}
            </span>
          </div>
        </div>
      </div>

      <LoadingWrapper isLoading={!!isLoading} loadingClassName="h-full">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 relative">
          {data ? (
            <>
              <div className="grid grid-cols-1 hd:grid-cols-2 hd:items-stretch gap-5">
                {/* Spatial Settings */}
                <div className="relative h-[11rem]">
                  <div
                    role="region"
                    aria-label="Spatial Settings"
                    key={`spatial-settings-${data.id}-${expanded}`} // Force re-mount when expanded state changes to reset scroll
                    className={cn(
                      "bg-white rounded-lg text-txtHeading flex flex-col gap-0 h-[11rem] border border-whisper relative overflow-hidden",
                      expanded &&
                        "absolute inset-x-0 top-0 h-auto max-h-[522px] z-[999] shadow-xl overflow-auto"
                    )}
                  >
                    <div ref={contentRef}>
                      <div className="bg-white-smoke p-4 rounded-t-lg flex items-center justify-between sticky top-0 z-10">
                        <p className="font-semibold text-sm">
                          Spatial Settings
                        </p>
                        {renderInputAreaType(data.area_input.type)}
                      </div>
                      {/* Input Administrative */}
                      {data.area_input.type ===
                        AnalysisHistoryInputType.ADMIN && (
                        <div className="flex flex-col gap-0 p-4 overflow-y-hidden">
                          <p className="font-semibold mb-2">
                            {adminHierarchy[0]?.name}
                          </p>
                          <div className="pr-2 space-y-1">
                            <DetailAdminTree
                              nodes={adminHierarchy[0]?.children || []}
                            />
                          </div>
                        </div>
                      )}
                      {/* Input Catchment */}
                      {data.area_input.type ===
                        AnalysisHistoryInputType.CATCHMENT && (
                        <div className="flex flex-col gap-2 p-4 overflow-y-hidden">
                          {/* Catchment Type */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <p className="text-txtBody">Catchment Type</p>
                              <p className="text-txtHeading">
                                {renderCatchmentType()}
                              </p>
                            </div>
                            {/* Routing */}
                            {byRoadAccess && (
                              <>
                                <div>
                                  <p className="text-txtBody">Routing</p>
                                  <p className="text-txtHeading capitalize">
                                    {data.area_input?.catchment?.profile}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-txtBody">Measurement</p>
                                  <p className="text-txtHeading capitalize">
                                    {data.area_input?.catchment?.metric}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-txtBody capitalize">
                                    {data.area_input?.catchment?.metric}
                                  </p>
                                  <p className="text-txtHeading capitalize">
                                    {data.area_input?.catchment?.contours?.join(
                                      ", "
                                    )}
                                  </p>
                                </div>

                                <div>
                                  <p className="text-txtBody">
                                    Road Exclusions
                                  </p>
                                  <p className="text-txtHeading capitalize">
                                    {onGetRoadExclusionValue()}
                                  </p>
                                </div>
                              </>
                            )}
                            {byRadius && (
                              <>
                                <div>
                                  <p className="text-txtBody">Measurement</p>
                                  <p className="text-txtHeading capitalize">
                                    {data.area_input?.catchment?.metric}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-txtBody">Radius</p>
                                  <p className="text-txtHeading capitalize">
                                    {data.area_input?.catchment?.contours?.join(
                                      ", "
                                    )}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>

                          <p className="font-semibold text-txtBody">
                            Catchment Selection
                          </p>
                          <div className="pr-2 space-y-2">
                            {/* Coordinate */}
                            <div>
                              <div className="text-neutral-600 space-y-1">
                                {data.area_input?.catchment?.locations?.map(
                                  (location) => (
                                    <div
                                      key={location.name}
                                      className="flex items-center gap-3"
                                    >
                                      <div className="h-5 w-5">
                                        <POIIcon className="h-5 w-5 fill-dim-gray" />
                                      </div>
                                      <div>
                                        <p className="text-txtHeading font-semibold">
                                          {location.name}
                                        </p>
                                        <span className="text-txtBody">
                                          ({location.latitude},{" "}
                                          {location.longitude})
                                        </span>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Input Polygon */}
                      {data.area_input.type ===
                        AnalysisHistoryInputType.POLYGON && (
                        <div className="flex flex-col gap-2 p-4 overflow-y-hidden">
                          <p className="font-semibold text-sm mb-2">
                            Polygon Selection
                          </p>
                          <div className="pr-2 space-y-2">
                            {polygons?.map((polygon) => (
                              <div
                                key={polygon?.name}
                                className="mb-2 flex items-center gap-3"
                              >
                                <div className="h-5 w-5">
                                  <MapPolygonIcon className="fill-dim-gray" />
                                </div>
                                <div>
                                  <p className="text-txtHeading font-semibold">
                                    {polygon?.name}:
                                  </p>
                                  <span className="text-neutral-600">
                                    {polygon?.coordinates}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {(needExpand || expanded) && (
                      <button
                        onClick={() => setExpanded((isExpanded) => !isExpanded)}
                        aria-label={
                          expanded
                            ? "Collapse Spatial Settings"
                            : "Expand Spatial Settings"
                        }
                        aria-expanded={expanded}
                        className="flex items-center justify-center w-full gap-1 p-1 bg-white/85 rounded-lg sticky bottom-0"
                      >
                        <span>{expanded ? "Collapse" : "Expand"}</span>
                        <ArrowDownIcon
                          className={twMerge(
                            "transition-transform fill-txtBody",
                            expanded && "rotate-180 fill-teal-700"
                          )}
                        />
                      </button>
                    )}
                  </div>
                </div>

                {/* Output Analysis */}
                <div
                  role="region"
                  aria-label="Output Analysis"
                  className="bg-white rounded-lg text-txtHeading flex flex-col gap-0 min-h-[11rem] border border-whisper overflow-hidden"
                >
                  <div className="bg-white-smoke p-4 rounded-t-lg flex items-center justify-between">
                    <p className="font-semibold text-sm">Output Analysis</p>
                    {renderOutputAreaType(data.analysis_output.output_type)}
                  </div>
                  {data.analysis_output.output_type === OutputType.H3 && (
                    <ResolutionSection
                      level={data.analysis_output.level}
                      coverageContent={getH3Coverage(
                        data.analysis_output.level
                      )}
                      customCatchmentRadiusM={catchmentRadiusM}
                    />
                  )}
                  {data.analysis_output.output_type === OutputType.GEOHASH && (
                    <ResolutionSection
                      level={data.analysis_output.level}
                      coverageContent={
                        <span className="text-txtHeading font-semibold">
                          {renderGeohashResolution(data.analysis_output.level)}
                        </span>
                      }
                      customCatchmentRadiusM={catchmentRadiusM}
                    />
                  )}
                  {data.analysis_output.output_type ===
                    OutputType.SITE_PROFILING && (
                    <div className="flex items-center justify-center flex-1 min-h-0 p-2">
                      <div className="bg-white-smoke border border-dashed border-whisper rounded-lg flex flex-col gap-2 items-center justify-center p-4">
                        <p className="text-txtBody text-[14px] font-semibold">
                          Site Profiling
                        </p>
                        <p className="text-txtBody text-sm text-center">
                          {MESSAGE.ANALYSIS_HISTORY_SITE_PROFILING_NO_DATA}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <AnalysisHistoryDatasetList key={data.id} data={data} />
              </div>
            </>
          ) : (
            <EmptyState
              title={emptyMessage.title}
              description={emptyMessage.desc}
            />
          )}
        </div>
      </LoadingWrapper>
      <div className="flex gap-2 shadow-2xl bg-white p-4 justify-center 2xl:justify-end">
        <Button
          variant="outline"
          className="w-[800px] 2xl:w-[400px] border-primary-teal text-primary-teal hover:bg-primary-teal hover:text-white rounded-xl"
          onClick={handleOnDelete}
          disabled={!data}
          aria-label="Delete Analysis"
        >
          Delete
        </Button>
        <Button
          className="w-[800px] 2xl:w-[400px] bg-primary-teal hover:bg-primary-teal/90 text-white rounded-xl"
          onClick={handleOnOpen}
          disabled={!data}
          aria-label="Open Analysis"
        >
          Open
        </Button>
      </div>
    </div>
  );
};
